import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { apiClient } from '../api/client'

export interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: 'admin' | 'bcba' | 'rbt' | 'billing' | 'parent' | 'platform_owner' | string
  role_display: string
  phone: string | null
  npi: string | null
  taxonomy_code: string | null
  is_active: boolean
  date_joined: string
  last_login: string | null
  is_superuser?: boolean
  is_staff?: boolean
  groups?: string[]
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  hasHydrated: boolean
  forcePasswordChange: boolean

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshAccessToken: () => Promise<void>
  setUser: (user: User) => void
  checkAuth: () => Promise<void>
  completeHydration: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      hasHydrated: false,
      forcePasswordChange: false,

      login: async (email: string, password: string) => {
        try {
          set({ isLoading: true })
          const response = await apiClient.post('/api/v1/auth/login/', {
            email,
            password,
          })

          const { access, refresh, user, force_password_change } = response.data

          set({
            user,
            accessToken: access,
            refreshToken: refresh,
            isAuthenticated: true,
            isLoading: false,
            forcePasswordChange: force_password_change,
          })

          // Set token in API client
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
      },

      logout: async () => {
        const { refreshToken } = get()

        try {
          if (refreshToken) {
            await apiClient.post('/api/v1/auth/logout/', {
              refresh: refreshToken,
            })
          }
        } catch (error) {
          // Ignore logout errors
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          forcePasswordChange: false,
        })

        delete apiClient.defaults.headers.common['Authorization']
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()

        if (!refreshToken) {
          throw new Error('No refresh token')
        }

        try {
          const response = await apiClient.post('/api/v1/auth/refresh/', {
            refresh: refreshToken,
          })

          const { access } = response.data

          set({ accessToken: access })
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`
        } catch (error) {
          // Refresh failed: logout only on explicit auth failures.
          if (axios.isAxiosError(error) && error.response) {
            await get().logout()
          }
          throw error
        }
      },

      setUser: (user: User) => {
        set({ user })
      },

      checkAuth: async () => {
        const { accessToken, refreshToken } = get()

        if (!accessToken || !refreshToken) {
          set({ isLoading: false, isAuthenticated: false, user: null })
          return
        }

        try {
          set({ isLoading: true })
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          const response = await apiClient.get('/api/v1/auth/me/')

          set({ user: response.data, isAuthenticated: true, isLoading: false })
        } catch (error) {
          // Network/offline: keep the existing authenticated state.
          if (axios.isAxiosError(error) && !error.response) {
            set({ isLoading: false })
            return
          }

          // Try to refresh token
          try {
            await get().refreshAccessToken()

            const response = await apiClient.get('/api/v1/auth/me/')
            set({ user: response.data, isAuthenticated: true, isLoading: false })
          } catch (refreshError) {
            if (axios.isAxiosError(refreshError) && !refreshError.response) {
              set({ isLoading: false })
              return
            }
            set({
              isLoading: false,
              isAuthenticated: false,
            })
          }
        }
      },

      completeHydration: () => {
        const { accessToken, refreshToken } = get()
        const isAuthenticated = Boolean(accessToken && refreshToken)
        if (accessToken) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        } else {
          delete apiClient.defaults.headers.common['Authorization']
        }

        set({ hasHydrated: true, isAuthenticated, isLoading: false })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.completeHydration()
      },
    }
  )
)

export const isPlatformOwner = (user: User | null): boolean => {
  if (!user) return false
  return (
    user.is_superuser === true ||
    user.is_staff === true ||
    user.role === 'platform_owner' ||
    user.role === 'admin' ||
    (Array.isArray(user.groups) && user.groups.includes('platform_owner'))
  )
}
